import { setCookie } from "cookies-next"
import isEqual from "lodash/isEqual"

import { env } from "@/lib/env"
import { isEmbedded } from "@/lib/is-embedded"
import { rememberPlaceSearchParams } from "@/lib/place-search-memory"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import { RootState } from "@/lib/storage/redux-store"
import userSlice, {
  UpdateSelectedPlaceSearchParamsPayload,
} from "@/lib/storage/user-slice"
import {
  addDaysToTimestamp,
  createJSDateFromTimestamp,
  now,
} from "../date-utils"
import { webStorage } from "../web-storage"

const persistMiddleware =
  createMtListenerMiddleware<RootState>("storage-persist")

persistMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    // only listening for uiSettingsSlice changes, this will trigger the effect to persist these changes
    return !isEqual(currentState.uiSettingsSlice, previousState.uiSettingsSlice)
  },
  effect: async (action, listenerApi) => {
    const currentState = listenerApi.getState()
    const uiSettings = JSON.stringify(currentState.uiSettingsSlice)
    const expires = addDaysToTimestamp(now(), 365 * 10) // don't expire
    // save UI settings to cookie or local storage
    if (isEmbedded()) {
      webStorage.setItem("mindtrip.uiSettings", uiSettings)
    } else {
      setCookie("mindtrip.uiSettings", uiSettings, {
        httpOnly: false,
        sameSite: "lax",
        secure: env("NEXT_PUBLIC_DEVELOPMENT") !== "true",
        expires: createJSDateFromTimestamp(expires),
      })
    }
  },
})

persistMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    if (
      isEqual(
        currentState.userSlice.selectedPlaceSearchParams,
        previousState.userSlice.selectedPlaceSearchParams
      )
    ) {
      return false
    }

    if (!currentState.userSlice.selectedPlaceSearchParams) {
      return false
    }

    if (
      action.type === userSlice.actions.updateSelectedPlaceSearchParams.type
    ) {
      const payload = action.payload as UpdateSelectedPlaceSearchParamsPayload
      if (payload.source === "tripSync") {
        return false
      }
    }

    return true
  },
  effect: async (_action, listenerApi) => {
    const params = listenerApi.getState().userSlice.selectedPlaceSearchParams
    if (params) {
      rememberPlaceSearchParams(params)
    }
  },
})

export { persistMiddleware }
